/**
 * Easify Box Builder cart reconciliation.
 *
 * Box Builder adds an add-on surcharge as a second cart line that shares an
 * `_sbb:boxKey` property with its box line. Shopify treats the two lines as
 * unrelated, so removing the box leaves the surcharge behind as an orphaned
 * charge the customer can still check out with.
 *
 * This module removes add-on lines whose box line is gone, and clears the
 * `sbb_*` cart attributes belonging to boxes that no longer exist so stale
 * engraving instructions do not reach the order.
 */

import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const BOX_KEY_PROPERTY = '_sbb:boxKey';
const ADDON_PRICE_PROPERTY = '_sbb:addonPriceValue';
const ROW_SELECTOR = '[data-easify-box-key]';
const BOX_ATTRIBUTE_PATTERN = /_idUnique_(.+)$/;
const CONFIGURATION_ATTRIBUTES = ['sbb_box_configurations', 'BoxSource'];
const SOURCE = 'easify-box-cart-sync';

let reconciling = false;
let reconcileQueued = false;

const getProperties = (item) => item.properties ?? {};

const isBoxLine = (item) => {
  const properties = getProperties(item);
  return Boolean(properties[BOX_KEY_PROPERTY]) && !properties[ADDON_PRICE_PROPERTY];
};

const isAddonLine = (item) => {
  const properties = getProperties(item);
  return Boolean(properties[BOX_KEY_PROPERTY]) && Boolean(properties[ADDON_PRICE_PROPERTY]);
};

async function getCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Cart fetch failed with ${response.status}`);
  return response.json();
}

async function removeLine(key) {
  const body = JSON.stringify({ id: key, quantity: 0 });
  const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
  if (!response.ok) throw new Error(`Cart change failed with ${response.status}`);
  return response.json();
}

async function clearAttributes(attributes) {
  const body = JSON.stringify({ attributes });
  const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
  if (!response.ok) throw new Error(`Cart update failed with ${response.status}`);
  return response.json();
}

function findOrphans(cart) {
  const liveBoxKeys = new Set(cart.items.filter(isBoxLine).map((item) => getProperties(item)[BOX_KEY_PROPERTY]));

  const orphanLines = cart.items.filter(
    (item) => isAddonLine(item) && !liveBoxKeys.has(getProperties(item)[BOX_KEY_PROPERTY])
  );

  return { liveBoxKeys, orphanLines };
}

function findStaleAttributes(cart, liveBoxKeys) {
  const attributes = cart.attributes ?? {};
  const stale = {};

  for (const key of Object.keys(attributes)) {
    const match = key.match(BOX_ATTRIBUTE_PATTERN);
    if (match && !liveBoxKeys.has(match[1])) stale[key] = '';
  }

  if (liveBoxKeys.size === 0) {
    for (const key of CONFIGURATION_ATTRIBUTES) {
      if (key in attributes) stale[key] = '';
    }
  }

  return stale;
}

async function reconcile() {
  if (reconciling) {
    reconcileQueued = true;
    return;
  }

  reconciling = true;

  try {
    const cart = await getCart();
    const { liveBoxKeys, orphanLines } = findOrphans(cart);
    const staleAttributes = findStaleAttributes(cart, liveBoxKeys);
    let updatedCart = null;

    for (const line of orphanLines) {
      updatedCart = await removeLine(line.key);
    }

    if (Object.keys(staleAttributes).length > 0) {
      updatedCart = await clearAttributes(staleAttributes);
    }

    if (updatedCart) {
      document.dispatchEvent(
        new CartUpdateEvent({}, SOURCE, { source: SOURCE, itemCount: updatedCart.item_count ?? 0 })
      );
    }
  } catch (error) {
    console.error('[easify-box-cart-sync]', error);
  } finally {
    reconciling = false;

    if (reconcileQueued) {
      reconcileQueued = false;
      reconcile();
    }
  }
}

/**
 * Cart rows carry the box key and add-on flag as data attributes, so the
 * companion line can be resolved synchronously at click time. Reading it from
 * the DOM rather than `/cart.js` is what allows both lines to be removed in a
 * single request instead of a fetch-then-remove round trip.
 *
 * @param {string | undefined} lineKey - Line item key of the row being removed.
 * @returns {string[]} Line item keys of add-on lines belonging to that box.
 */
export function getEasifyCompanionKeys(lineKey) {
  if (!lineKey) return [];

  const rows = Array.from(document.querySelectorAll(ROW_SELECTOR));
  const source = rows.find((row) => row.dataset.key === lineKey);

  // Only removing the box line cascades. Removing the add-on alone is left to
  // the merchant's chosen behaviour.
  if (!source || source.dataset.easifyAddon === 'true') return [];

  const boxKey = source.dataset.easifyBoxKey;
  if (!boxKey) return [];

  const companionKeys = rows
    .filter(
      (row) => row.dataset.easifyAddon === 'true' && row.dataset.easifyBoxKey === boxKey && Boolean(row.dataset.key)
    )
    .map((row) => row.dataset.key);

  return Array.from(new Set(companionKeys));
}

/**
 * Points each add-on row at its box row via `data-parent-key`, which is the
 * relationship the cart component already uses to animate nested bundle lines
 * out together.
 */
export function linkAddonRows() {
  const rows = Array.from(document.querySelectorAll(ROW_SELECTOR));
  const boxLineKeys = new Map();

  for (const row of rows) {
    if (row.dataset.easifyAddon === 'true') continue;
    if (row.dataset.easifyBoxKey && row.dataset.key) boxLineKeys.set(row.dataset.easifyBoxKey, row.dataset.key);
  }

  for (const row of rows) {
    if (row.dataset.easifyAddon !== 'true') continue;

    const parentKey = boxLineKeys.get(row.dataset.easifyBoxKey);
    if (parentKey && row.dataset.parentKey !== parentKey) row.dataset.parentKey = parentKey;
  }
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  requestAnimationFrame(linkAddonRows);

  if (event.detail?.data?.source === SOURCE) return;
  reconcile();
});

linkAddonRows();

if (Theme.template?.name === 'cart') reconcile();
